import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, catchError, of, throwError } from 'rxjs';
import { AuthService } from 'src/app/services/auth.service';
import { CookieStore } from 'src/app/shared/helpers/CookieStore';
import { CommunicationService } from 'src/app/services/communication.service';
import { Defaultconfig } from 'src/app/shared/models/Defaultconfig';
import { NavigationEnd, Router } from '@angular/router';
import { ToastService } from 'src/app/services/toast/toast-service';

/**
 * License lockout codes that map 1:1 to the LicenseErrorCode enum in
 * skill-auth-sdk. When the server returns 403 with one of these (either in
 * the X-License-Status response header or in the body's `code` field), the
 * interceptor stashes the context and routes to /license-locked.
 *
 * USER_LIMIT_EXCEEDED is intentionally excluded — that's an inline 409 on
 * the registration endpoint, handled by RegisterComponent, not a full-page
 * lockout.
 */
const LICENSE_LOCKOUT_CODES = [
  'LICENSE_REVOKED',
  'LICENSE_EXPIRED',
  'LICENSE_SUSPENDED',
  'LICENSE_UNAVAILABLE',
];

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  token: any;
  resIds: any;
  redirectUrl: any;

   // debounce flag so a burst of 429s surfaces a single toast, not one per request
   private static rateLimitNotified = false;
   /** Prevents redirect storms if multiple parallel calls all fail. */
   private static lockoutInProgress = false;

  constructor(private authService: AuthService, private communicationService: CommunicationService,
    private router: Router ,private toast: ToastService) {
    this.router.events.subscribe((event: any) => {
      if (event instanceof NavigationEnd) {
        this.communicationService.queryParams$.subscribe((data: any) => {
          //console.log('router=', data);
          this.token = data.access_token;
          // CookieStore.saveBearerToken(this.token);
          localStorage.setItem('access_token', this.token);
        });
      }
    });
  }


 intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
  return next.handle(req).pipe(
    catchError((err: any) => {
      if (err instanceof HttpErrorResponse) {
        // License lockout — handled before the generic 401 path so a
        // license-revoked 403 doesn't fall through to the logout flow.
        const onLockoutPage = window.location.pathname === '/license-locked';
        if (err.status === 403 && !AuthInterceptor.lockoutInProgress && !onLockoutPage) {
          const code = this.extractLicenseCode(err);
          if (code) {
            AuthInterceptor.lockoutInProgress = true;
            this.redirectToLicenseLockout(code, err.error);
            return of();
          }
        }

        // On the lockout page OR a LICENSE_* 403 — swallow so background
        // calls don't trigger the generic 401 logout fallback.
        if (
          err.status === 403 &&
          (onLockoutPage || this.extractLicenseCode(err) !== null)
        ) {
          return of();
        }

        if (err.status === 429) {
          // Rate limited. Surface a clear, non-destructive toast (no page reload, so any
          // in-progress form input is preserved) and swallow the error.
          const retryAfter = err.headers?.get('retry-after');
          const detail = retryAfter
            ? `You've made too many requests. Please try again in ${retryAfter} second(s).`
            : `You've made too many requests. Please wait a moment and try again.`;

          if (!AuthInterceptor.rateLimitNotified) {
            AuthInterceptor.rateLimitNotified = true;
            this.toast.error('Too many requests', detail);
            setTimeout(() => { AuthInterceptor.rateLimitNotified = false; }, 5000);
          }
          return of();
        } else {
          this.handleAuthError(err);
        }
      }
      return throwError(() => err);
    })
  );
}

  /**
   * Look for a LICENSE_* code on the 403 response. The server emits it both
   * as the `X-License-Status` header and inside the JSON body (`code`), so
   * the interceptor checks the header first and falls back to the body.
   */
  private extractLicenseCode(err: HttpErrorResponse): string | null {
    const headerCode = err.headers?.get?.('x-license-status') ?? err.headers?.get?.('X-License-Status');
    if (headerCode && LICENSE_LOCKOUT_CODES.includes(headerCode)) {
      return headerCode;
    }
    const bodyCode = (err.error && (err.error.code ?? err.error?.error?.code)) as string | undefined;
    if (bodyCode && LICENSE_LOCKOUT_CODES.includes(bodyCode)) {
      return bodyCode;
    }
    return null;
  }

  private redirectToLicenseLockout(code: string, body: any): void {
    try {
      sessionStorage.setItem('license_lockout_reason', code);
      sessionStorage.setItem('license_lockout_data', JSON.stringify(body?.license ?? body ?? {}));
    } catch {
      // sessionStorage can throw in private-browsing modes — proceed anyway.
    }
    // Full reload so any partially-loaded state from the failed call is
    // discarded — the user lands on a clean lockout page.
    window.location.href = '/license-locked';
  }

  handleAuthError(error: HttpErrorResponse): Observable<any> | any {
    //console.log('handleAuthError', error)
    if (error.status === 401) {
      // if (this.token) {
        setTimeout(() => {
          this.logout();
          this.toast.error("Access Denied: You do not have permission to access this site", "error")
        }, 5000);
        
      // }
      return of(error.message);
    }
    return throwError(error);
  }

  getAllIds() {
    this.authService.getIdsForLogout(this.token).subscribe((res: any) => {
      //console.log('Ids', res);
      if (res && res.data) {
        this.resIds = res.data;
        const macRef = localStorage.getItem('x_t_nbd_ref');
        this.redirectUrl = Defaultconfig.getCurrentUrl() + '/login?view_type=login&request_id=' + this.resIds.request_id + '&client_id=' + this.resIds.client_id + '&flow_id=' + this.resIds.flow_id + '&x_t_nbd_ref=' + macRef;
        //console.log('url', this.redirectUrl);
        localStorage.setItem('post_url', this.redirectUrl);
      }
    });
  }


  logout() {
    this.redirectUrl = localStorage.getItem('post_url')
    this.authService.logout(this.token, encodeURIComponent(this.redirectUrl)).subscribe((res: any) => {
      //console.log(res);
    });
    window.open(this.redirectUrl, '_self');

  }

}
